import { getTrackingParams } from './tracking'
import { getEfTransactionId } from './everflow'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

// ---- Post-auth navigation config (GB flag: createsexfriends-white-paywall-nav-config) ----
// Flag value is JSON: { type: 'paywall' | 'chat_paywall' }.
//   'paywall'      → keep the existing /paywall handoff (default).
//   'chat_paywall' → send the user to /lobby instead, still carrying model-params.
//
// Read the persisted nav type. GrowthBookProvider writes the resolved flag value to
// localStorage on mount, so it's available to every redirect builder here — including the
// post-OAuth return handler, which runs before GrowthBook re-initializes.
export const getNavType = () => {
  try {
    return localStorage.getItem('paywallNavType') || 'paywall'
  } catch {
    return 'paywall'
  }
}

// 'chat_paywall' → /lobby; anything else (incl. the default 'paywall') keeps /paywall.
export const resolvePaywallPath = navType =>
  navType === 'chat_paywall' ? '/lobby' : '/paywall'

// ---- Geo pricing (GB flag: location-based-price-multiplier) ----
// The backend multiplies a plan up from
// `user.marketing.experiments['location-based-price-multiplier']` when it builds the
// Upgate charge, but GET /subscription-plans only applies it for an authenticated
// user — and the popup's plans call is cross-origin, x-api-key-only, so the plans we
// render carry the BASE prices. Any amount the lander prints into the checkout text
// therefore has to be multiplied here or it understates what is actually billed.
export const LOCATION_PRICE_MULTIPLIER_FLAG = 'location-based-price-multiplier'
export const DEFAULT_PRICE_MULTIPLIER = 1

// The GB flag is number-typed. Still coerced and range-checked rather than used raw:
// an unset flag arrives as undefined, and a non-positive value would zero out or
// invert a price, so anything not > 0 falls back to 1.
export const resolvePriceMultiplier = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PRICE_MULTIPLIER
}

// MUST match the backend's shared/utils/applyMultiplier exactly — that is what
// computes the amount actually charged. Diverging rounding here would print a price
// the visitor is never billed.
export const applyPriceMultiplier = (price, multiplier = DEFAULT_PRICE_MULTIPLIER) => {
  if (!price) return price
  return Math.ceil(price * multiplier) - 0.01
}

// Build the `path` baked into the Google OAuth state. Backend redirects to BASE + path
// after auth, so the destination must be embedded here too.
// create-friend-white is the SFW lander variant — protection=sfw rides along.
export const buildOAuthStatePath = navType => {
  const params = new URLSearchParams()
  params.set('protection', 'sfw')
  // chat_paywall → /lobby: tag the handoff so the lobby flow/analytics can identify the source.
  if (navType === 'chat_paywall') params.set('type', 'chat_paywall')
  return `${resolvePaywallPath(navType)}?${params.toString()}`
}

export const buildPaywallUrl = (authToken, authType = 'login_paywall', modelParams = null, style = 'Realistic', pw) => {
  const navType = getNavType()
  const paywallUrl = new URL(SWIPEY_BASE_URL + resolvePaywallPath(navType))

  paywallUrl.searchParams.set('auth_token', authToken)
  paywallUrl.searchParams.set('auth_type', authType)
  // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
  paywallUrl.searchParams.set('protection', 'sfw')

  const typeValue = style === 'Anime' ? 'anime' : 'realistic'
  paywallUrl.searchParams.set('type', typeValue)

  const efTransactionId = getEfTransactionId()
  if (efTransactionId) {
    paywallUrl.searchParams.set('ef_transaction_id', efTransactionId)
  }

  const trackingParams = getTrackingParams()
  Object.entries(trackingParams).forEach(([key, value]) => {
    if (value) paywallUrl.searchParams.set(key, value)
  })

  if (modelParams) paywallUrl.searchParams.set('model-params', modelParams)
  if (pw) {
    paywallUrl.searchParams.set('media', 'vid.mp4')
    paywallUrl.searchParams.set('pw_v', pw)
  }

  // chat_paywall → /lobby: set type=chat_paywall LAST so it wins over the style type AND the
  // tracking spread above (getTrackingParams forwards the landing URL's ?type=). /lobby uses model-params.
  if (navType === 'chat_paywall') paywallUrl.searchParams.set('type', 'chat_paywall')

  return paywallUrl.toString()
}
